export interface ModelProvider {
  id: string;
  name: string;
  icon: string; // Icon name from @lobehub/icons
}

export interface Model {
  id: string;
  name: string;
  provider: string; // Provider ID (e.g., 'claude')
  providerName: string; // Provider display name (e.g., 'Anthropic Claude')
  paramKey: string; // The key used in params (e.g., 'modelsAnthropicClaude')
}

export interface ModelSelection {
  provider: string; // llmProvider value (e.g., 'claude')
  model: string; // model id
  paramKey: string; // The param key to use (e.g., 'modelsAnthropicClaude')
  isCustomModel?: boolean; // Explicit flag to indicate if a custom model is selected
  customModelName?: string; // Custom model name when provider is 'custom'
  customProvider?: string; // Specific custom provider (e.g., 'azureopenai', 'amazonbedrock') when provider is 'custom'
}

// Provider constants to avoid hardcoded strings
export const PROVIDER_OPENAI = 'openai';
export const PROVIDER_ANTHROPIC = 'claude';
export const PROVIDER_GOOGLE = 'gemini';
export const PROVIDER_DEEPSEEK = 'deepseek';
export const PROVIDER_VERTEX_AI = 'vertex_ai';
export const PROVIDER_HUGGINGFACE = 'huggingface';
export const PROVIDER_AZURE_OPENAI = 'azureopenai';
export const PROVIDER_AMAZON_BEDROCK = 'amazonbedrock';
export const PROVIDER_CUSTOM = 'custom';

/** Static catalog id for the generic “Custom Model” row */
export const CUSTOM_MODEL_ENTRY_ID = 'custom:custom-model' as const;

// Custom provider model IDs (these models use normal providers but are custom/private)
// Note: These constants are kept for backward compatibility but the models now use
// PROVIDER_AZURE_OPENAI and PROVIDER_AMAZON_BEDROCK as providers, with useCustomModel=true
export const CUSTOM_MODEL_AZURE_OPENAI = 'azureopenai:private-model';
export const CUSTOM_MODEL_AMAZON_BEDROCK = 'amazonbedrock:private-model';

// Deprecated: These constants are kept for backward compatibility but should not be used
// Custom models now use normal providers (azureopenai/amazonbedrock) with useCustomModel boolean
export const CUSTOM_PROVIDER_AZURE_OPENAI = 'custom-azureopenai';
export const CUSTOM_PROVIDER_AMAZON_BEDROCK = 'custom-amazonbedrock';

// Default models
export const DEFAULT_MODEL_OPENAI = 'gpt-4o';
export const DEFAULT_MODEL_ANTHROPIC = 'claude-3-7-sonnet-20250219';

export const MODEL_PROVIDERS: Record<string, ModelProvider> = {
  [PROVIDER_ANTHROPIC]: {
    id: PROVIDER_ANTHROPIC,
    name: 'Anthropic Claude',
    icon: 'Anthropic' // Icon name from @lobehub/icons
  },
  [PROVIDER_DEEPSEEK]: {
    id: PROVIDER_DEEPSEEK,
    name: 'DeepSeek',
    icon: 'DeepSeek'
  },
  [PROVIDER_GOOGLE]: {
    id: PROVIDER_GOOGLE,
    name: 'Google Gemini',
    icon: 'Gemini'
  },
  [PROVIDER_OPENAI]: {
    id: PROVIDER_OPENAI,
    name: 'OpenAI',
    icon: 'OpenAI'
  },
  [PROVIDER_VERTEX_AI]: {
    id: PROVIDER_VERTEX_AI,
    name: 'Google Vertex AI',
    icon: 'Google'
  },
  [PROVIDER_HUGGINGFACE]: {
    id: PROVIDER_HUGGINGFACE,
    name: 'Hugging Face',
    icon: 'HuggingFace'
  },
  [PROVIDER_AZURE_OPENAI]: {
    id: PROVIDER_AZURE_OPENAI,
    name: 'Azure OpenAI',
    icon: 'Azure'
  },
  [PROVIDER_AMAZON_BEDROCK]: {
    id: PROVIDER_AMAZON_BEDROCK,
    name: 'Amazon Bedrock',
    icon: 'Bedrock'
  },
  [PROVIDER_CUSTOM]: {
    id: PROVIDER_CUSTOM,
    name: 'Custom Model',
    icon: 'Custom'
  }
};

// All models in a flat array with provider information
export const ALL_MODELS: Model[] = [
  // Anthropic Claude
  {
    id: 'claude-opus-4-5-20251101',
    name: 'Claude Opus 4.5',
    provider: 'claude',
    providerName: 'Anthropic Claude',
    paramKey: 'modelsAnthropicClaude'
  },
  {
    id: 'claude-haiku-4-5-20251001',
    name: 'Claude Haiku 4.5',
    provider: 'claude',
    providerName: 'Anthropic Claude',
    paramKey: 'modelsAnthropicClaude'
  },
  {
    id: 'claude-sonnet-4-5-20250929',
    name: 'Claude Sonnet 4.5',
    provider: 'claude',
    providerName: 'Anthropic Claude',
    paramKey: 'modelsAnthropicClaude'
  },
  {
    id: 'claude-opus-4-1-20250805',
    name: 'Claude Opus 4.1',
    provider: 'claude',
    providerName: 'Anthropic Claude',
    paramKey: 'modelsAnthropicClaude'
  },
  {
    id: 'claude-opus-4-20250514',
    name: 'Claude Opus 4',
    provider: 'claude',
    providerName: 'Anthropic Claude',
    paramKey: 'modelsAnthropicClaude'
  },
  {
    id: 'claude-sonnet-4-20250514',
    name: 'Claude Sonnet 4',
    provider: 'claude',
    providerName: 'Anthropic Claude',
    paramKey: 'modelsAnthropicClaude'
  },
  {
    id: 'claude-3-7-sonnet-20250219',
    name: 'Claude Sonnet 3.7',
    provider: 'claude',
    providerName: 'Anthropic Claude',
    paramKey: 'modelsAnthropicClaude'
  },
  {
    id: 'claude-3-5-haiku-20241022',
    name: 'Claude Haiku 3.5',
    provider: 'claude',
    providerName: 'Anthropic Claude',
    paramKey: 'modelsAnthropicClaude'
  },
  {
    id: 'claude-3-haiku-20240307',
    name: 'Claude Haiku 3',
    provider: 'claude',
    providerName: 'Anthropic Claude',
    paramKey: 'modelsAnthropicClaude'
  },
  // DeepSeek
  {
    id: 'deepseek-v3.1-terminus',
    name: 'DeepSeek 3.1 Terminus',
    provider: 'deepseek',
    providerName: 'DeepSeek',
    paramKey: 'modelsDeepSeek'
  },
  {
    id: 'deepseek-r1-0528',
    name: 'DeepSeek R1',
    provider: 'deepseek',
    providerName: 'DeepSeek',
    paramKey: 'modelsDeepSeek'
  },
  // Google Gemini
  {
    id: 'gemini-2.5-pro-preview-05-06',
    name: 'Gemini 2.5 Pro Preview',
    provider: 'gemini',
    providerName: 'Google Gemini',
    paramKey: 'modelsGoogleGemini'
  },
  {
    id: 'gemini-2.5-flash-preview-09-2025',
    name: 'Gemini 2.5 Flash Preview',
    provider: 'gemini',
    providerName: 'Google Gemini',
    paramKey: 'modelsGoogleGemini'
  },
  {
    id: 'gemini-2.5-flash',
    name: 'Gemini 2.5 Flash',
    provider: 'gemini',
    providerName: 'Google Gemini',
    paramKey: 'modelsGoogleGemini'
  },
  // OpenAI
  {
    id: 'gpt-4',
    name: 'GPT-4',
    provider: 'openai',
    providerName: 'OpenAI',
    paramKey: 'modelsOpenAI'
  },
  {
    id: 'gpt-4.1',
    name: 'GPT-4.1',
    provider: 'openai',
    providerName: 'OpenAI',
    paramKey: 'modelsOpenAI'
  },
  {
    id: 'gpt-4.1-mini',
    name: 'GPT-4.1 Mini',
    provider: 'openai',
    providerName: 'OpenAI',
    paramKey: 'modelsOpenAI'
  },
  {
    id: 'gpt-4.1-nano',
    name: 'GPT-4.1 Nano',
    provider: 'openai',
    providerName: 'OpenAI',
    paramKey: 'modelsOpenAI'
  },
  {
    id: 'gpt-5',
    name: 'GPT-5',
    provider: 'openai',
    providerName: 'OpenAI',
    paramKey: 'modelsOpenAI'
  },
  {
    id: 'gpt-5-mini',
    name: 'GPT-5 Mini',
    provider: 'openai',
    providerName: 'OpenAI',
    paramKey: 'modelsOpenAI'
  },
  {
    id: 'gpt-5-nano',
    name: 'GPT-5 Nano',
    provider: 'openai',
    providerName: 'OpenAI',
    paramKey: 'modelsOpenAI'
  },
  {
    id: 'o3-pro',
    name: 'o3 Pro',
    provider: 'openai',
    providerName: 'OpenAI',
    paramKey: 'modelsOpenAI'
  },
  {
    id: 'o4-mini',
    name: 'o4 Mini',
    provider: 'openai',
    providerName: 'OpenAI',
    paramKey: 'modelsOpenAI'
  },
  {
    id: 'gpt-4o',
    name: 'GPT-4o',
    provider: 'openai',
    providerName: 'OpenAI',
    paramKey: 'modelsOpenAI'
  },
  {
    id: 'gpt-4o-mini',
    name: 'GPT-4o-mini',
    provider: 'openai',
    providerName: 'OpenAI',
    paramKey: 'modelsOpenAI'
  },
  {
    id: 'gpt-3.5-turbo',
    name: 'GPT-3.5-turbo',
    provider: 'openai',
    providerName: 'OpenAI',
    paramKey: 'modelsOpenAI'
  },
  // Azure OpenAI
  {
    id: 'azureopenai:gpt-5',
    name: 'GPT-5',
    provider: 'azureopenai',
    providerName: 'Azure OpenAI',
    paramKey: 'modelsAzureOpenAi'
  },
  {
    id: 'azureopenai:gpt-5-mini',
    name: 'GPT-5-mini',
    provider: 'azureopenai',
    providerName: 'Azure OpenAI',
    paramKey: 'modelsAzureOpenAi'
  },
  {
    id: 'azureopenai:gpt-5-nano',
    name: 'GPT-5-nano',
    provider: 'azureopenai',
    providerName: 'Azure OpenAI',
    paramKey: 'modelsAzureOpenAi'
  },
  {
    id: 'azureopenai:gpt-4.1',
    name: 'GPT-4.1',
    provider: 'azureopenai',
    providerName: 'Azure OpenAI',
    paramKey: 'modelsAzureOpenAi'
  },
  {
    id: 'azureopenai:gpt-4.1-mini',
    name: 'GPT-4.1-mini',
    provider: 'azureopenai',
    providerName: 'Azure OpenAI',
    paramKey: 'modelsAzureOpenAi'
  },
  {
    id: 'azureopenai:gpt-4.1-nano',
    name: 'GPT-4.1-nano',
    provider: 'azureopenai',
    providerName: 'Azure OpenAI',
    paramKey: 'modelsAzureOpenAi'
  },
  {
    id: 'azureopenai:gpt-4o',
    name: 'GPT-4o',
    provider: 'azureopenai',
    providerName: 'Azure OpenAI',
    paramKey: 'modelsAzureOpenAi'
  },
  // Custom Model
  {
    id: CUSTOM_MODEL_ENTRY_ID,
    name: 'Custom Model',
    provider: 'custom',
    providerName: 'Custom',
    paramKey: 'customModel'
  },
  // Custom Azure OpenAI (uses azureopenai provider with custom model name)
  {
    id: CUSTOM_MODEL_AZURE_OPENAI,
    name: 'Private Azure OpenAI Model',
    provider: PROVIDER_AZURE_OPENAI,
    providerName: 'Azure OpenAI (Private)',
    paramKey: 'modelsAzureOpenAi'
  },
  // Custom Amazon Bedrock (uses amazonbedrock provider with custom model name)
  {
    id: CUSTOM_MODEL_AMAZON_BEDROCK,
    name: 'Private Amazon Bedrock Model',
    provider: PROVIDER_AMAZON_BEDROCK,
    providerName: 'Amazon Bedrock (Private)',
    paramKey: 'modelsAmazonBedrock'
  }
];

// Helper to get models grouped by provider
export const MODELS_BY_PROVIDER: Record<string, Model[]> = ALL_MODELS.reduce(
  (acc, model) => {
    if (!acc[model.provider]) {
      acc[model.provider] = [];
    }
    acc[model.provider].push(model);
    return acc;
  },
  {} as Record<string, Model[]>
);

// Helper to find a model by ID
export function findModelById(modelId: string): Model | undefined {
  return ALL_MODELS.find(m => m.id === modelId);
}
